import React, { useState } from 'react';
import api from '../api';

const US_STATES = ['Alabama','Alaska','Arizona','Arkansas','California','Colorado','Connecticut','Delaware','Florida','Georgia','Hawaii','Idaho','Illinois','Indiana','Iowa','Kansas','Kentucky','Louisiana','Maine','Maryland','Massachusetts','Michigan','Minnesota','Mississippi','Missouri','Montana','Nebraska','Nevada','New Hampshire','New Jersey','New Mexico','New York','North Carolina','North Dakota','Ohio','Oklahoma','Oregon','Pennsylvania','Rhode Island','South Carolina','South Dakota','Tennessee','Texas','Utah','Vermont','Virginia','Washington','West Virginia','Wisconsin','Wyoming'];
const CANADIAN_PROVINCES = ['Alberta','British Columbia','Manitoba','New Brunswick','Newfoundland and Labrador','Northwest Territories','Nova Scotia','Nunavut','Ontario','Prince Edward Island','Quebec','Saskatchewan','Yukon'];

const inp = { width: '100%', padding: '0.6rem', background: 'var(--dark)', border: '1px solid var(--border)', borderRadius: '2px', color: 'var(--text)', fontFamily: 'Crimson Text, serif', fontSize: '1rem', boxSizing: 'border-box' };
const lbl = { display: 'block', marginBottom: '0.4rem', color: 'var(--text-muted)', fontFamily: 'Cinzel, serif', fontSize: '0.8rem', letterSpacing: '0.05em' };
const section = { background: 'var(--dark-2)', border: '1px solid var(--border)', borderRadius: '4px', padding: '1.5rem', marginBottom: '1.5rem' };
const gr2 = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' };
const errStyle = { color: '#ff6b6b', fontSize: '0.85rem', marginTop: '0.25rem', display: 'block' };

function GroupRegistrationForm() {
  const [formData, setFormData] = useState({
    group_name: '', primary_contact_name: '', primary_contact_email: '',
    mailing_street1: '', mailing_street2: '', mailing_city: '', mailing_state: '', mailing_zip: '', mailing_country: 'USA',
    year_established: '', city: '', state: '', geographic_description: '',
    group_bio: '', event_types: '', resources: '',
    accepts_new_members: '', membership_criteria: '',
    website: '', facebook: '', instagram: '', tiktok: '', youtube: '', other_social: '',
    publish_contact_name: false, publish_contact_email: false, publish_mailing_address: false,
    comments: ''
  });
  const [photos, setPhotos] = useState([]);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const set = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: null }));
  };

  const handlePhotoChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 3) { setErrors(prev => ({ ...prev, photos: 'Maximum 3 photos allowed.' })); return; }
    const oversized = files.filter(f => f.size > 20 * 1024 * 1024);
    if (oversized.length > 0) { setErrors(prev => ({ ...prev, photos: 'One or more files exceed the 20MB size limit.' })); return; }
    setPhotos(files);
    setErrors(prev => ({ ...prev, photos: null }));
  };

  const validate = () => {
    const errs = {};
    if (!formData.group_name.trim()) errs.group_name = 'Group name is required.';
    if (!formData.primary_contact_name.trim()) errs.primary_contact_name = 'Contact name is required.';
    if (!formData.primary_contact_email.trim()) errs.primary_contact_email = 'Contact email is required.';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.primary_contact_email)) errs.primary_contact_email = 'Invalid email format.';
    if (!formData.mailing_street1.trim()) errs.mailing_street1 = 'Street address is required.';
    if (!formData.mailing_city.trim()) errs.mailing_city = 'City is required.';
    if (!formData.mailing_state) errs.mailing_state = 'State/Province is required.';
    if (!formData.mailing_zip.trim()) errs.mailing_zip = 'ZIP/Postal code is required.';
    if (!formData.city.trim()) errs.city = 'City is required.';
    if (!formData.state) errs.state = 'State/Province is required.';
    if (!formData.group_bio.trim()) errs.group_bio = 'Group bio is required.';
    if (photos.length === 0) errs.photos = 'At least 1 photo is required.';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) { window.scrollTo(0, 0); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.keys(formData).forEach(key => fd.append(key, formData[key]));
      photos.forEach(photo => fd.append('photos', photo));
      const token = localStorage.getItem('token');
      await api.post('/api/groups/register', fd, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      setSuccess(true);
      window.scrollTo(0, 0);
    } catch (error) {
      alert('Submission failed: ' + (error.response?.data?.error || error.message));
    } finally {
      setSubmitting(false);
    }
  };

  const stateSelect = (field) => (
    <select style={{ ...inp, borderColor: errors[field] ? '#ff6b6b' : 'var(--border)' }} value={formData[field]} onChange={e => set(field, e.target.value)}>
      <option value="">Select...</option>
      <optgroup label="United States">{US_STATES.map(s => <option key={s} value={s}>{s}</option>)}</optgroup>
      <optgroup label="Canada">{CANADIAN_PROVINCES.map(p => <option key={p} value={p}>{p}</option>)}</optgroup>
    </select>
  );

  if (success) {
    return (
      <div style={{ maxWidth: '600px', margin: '3rem auto', padding: '2rem', background: 'var(--dark-2)', border: '1px solid #2d6a2d', borderRadius: '4px', textAlign: 'center' }}>
        <h2 style={{ color: '#90ee90' }}>✓ Registration Submitted!</h2>
        <p>Thank you for registering your Krampus group with Krampus America!</p>
        <p style={{ color: 'var(--text-muted)' }}>Your registration has been submitted for review. Once approved, it will appear on the Krampus America website.</p>
        <p style={{ color: 'var(--text-muted)' }}>Check your <a href="/dashboard">Dashboard</a> for status updates.</p>
        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', marginTop: '1.5rem' }}>
          <a href="/dashboard" className="btn btn-primary">Go to Dashboard</a>
          <a href="/groups" className="btn btn-secondary">View Groups</a>
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '800px', margin: '0 auto', padding: '2rem' }}>
      <h1>Group Registration</h1>
      <p style={{ color: 'var(--text-muted)', marginBottom: '2rem' }}>
        Register your Krampus group or troupe. All registrations are reviewed before being published. Fields marked with * are required.
      </p>

      {Object.keys(errors).length > 0 && (
        <div style={{ padding: '1rem', marginBottom: '1.5rem', background: 'rgba(139,0,0,0.2)', border: '1px solid var(--primary)', borderRadius: '4px', color: '#ff9999' }}>
          Please correct the errors below before submitting.
        </div>
      )}

      <form onSubmit={handleSubmit}>

        {/* Private Info */}
        <div style={section}>
          <h2 style={{ marginTop: 0 }}>Private Information</h2>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', fontStyle: 'italic' }}>This information is for Krampus America use only and will NOT be displayed publicly.</p>

          <div style={{ marginBottom: '1rem' }}>
            <label style={lbl}>Primary Contact Name *</label>
            <input style={{ ...inp, borderColor: errors.primary_contact_name ? '#ff6b6b' : 'var(--border)' }} value={formData.primary_contact_name} onChange={e => set('primary_contact_name', e.target.value)} />
            {errors.primary_contact_name && <span style={errStyle}>{errors.primary_contact_name}</span>}
          </div>
          <div style={{ marginBottom: '1rem' }}>
            <label style={lbl}>Primary Contact Email *</label>
            <input type="email" style={{ ...inp, borderColor: errors.primary_contact_email ? '#ff6b6b' : 'var(--border)' }} value={formData.primary_contact_email} onChange={e => set('primary_contact_email', e.target.value)} />
            {errors.primary_contact_email && <span style={errStyle}>{errors.primary_contact_email}</span>}
          </div>

          <h3 style={{ color: 'var(--accent)' }}>Mailing Address</h3>
          <div style={{ marginBottom: '1rem' }}>
            <label style={lbl}>Street Address 1 *</label>
            <input style={{ ...inp, borderColor: errors.mailing_street1 ? '#ff6b6b' : 'var(--border)' }} value={formData.mailing_street1} onChange={e => set('mailing_street1', e.target.value)} placeholder="123 Main St" />
            {errors.mailing_street1 && <span style={errStyle}>{errors.mailing_street1}</span>}
          </div>
          <div style={{ marginBottom: '1rem' }}>
            <label style={lbl}>Street Address 2 (Optional)</label>
            <input style={inp} value={formData.mailing_street2} onChange={e => set('mailing_street2', e.target.value)} placeholder="Suite, PO Box, etc." />
          </div>
          <div style={{ ...gr2, marginBottom: '1rem' }}>
            <div>
              <label style={lbl}>City *</label>
              <input style={{ ...inp, borderColor: errors.mailing_city ? '#ff6b6b' : 'var(--border)' }} value={formData.mailing_city} onChange={e => set('mailing_city', e.target.value)} />
              {errors.mailing_city && <span style={errStyle}>{errors.mailing_city}</span>}
            </div>
            <div>
              <label style={lbl}>State / Province *</label>
              {stateSelect('mailing_state')}
              {errors.mailing_state && <span style={errStyle}>{errors.mailing_state}</span>}
            </div>
          </div>
          <div style={{ ...gr2, marginBottom: '1rem' }}>
            <div>
              <label style={lbl}>ZIP / Postal Code *</label>
              <input style={{ ...inp, borderColor: errors.mailing_zip ? '#ff6b6b' : 'var(--border)' }} value={formData.mailing_zip} onChange={e => set('mailing_zip', e.target.value)} />
              {errors.mailing_zip && <span style={errStyle}>{errors.mailing_zip}</span>}
            </div>
            <div>
              <label style={lbl}>Country</label>
              <select style={inp} value={formData.mailing_country} onChange={e => set('mailing_country', e.target.value)}>
                <option value="USA">USA</option><option value="Canada">Canada</option>
              </select>
            </div>
          </div>
        </div>

        {/* Public Info */}
        <div style={section}>
          <h2 style={{ marginTop: 0 }}>Public Profile</h2>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', fontStyle: 'italic' }}>This information will be displayed on the Krampus America website.</p>

          <div style={{ marginBottom: '1rem' }}>
            <label style={lbl}>Group / Troupe Name *</label>
            <input style={{ ...inp, borderColor: errors.group_name ? '#ff6b6b' : 'var(--border)' }} value={formData.group_name} onChange={e => set('group_name', e.target.value)} />
            {errors.group_name && <span style={errStyle}>{errors.group_name}</span>}
          </div>
          <div style={{ marginBottom: '1rem' }}>
            <label style={lbl}>Year Established</label>
            <input style={inp} value={formData.year_established} onChange={e => set('year_established', e.target.value)} placeholder="e.g. 2015" />
          </div>

          <h3 style={{ color: 'var(--accent)' }}>Location (for public display)</h3>
          <div style={{ ...gr2, marginBottom: '1rem' }}>
            <div>
              <label style={lbl}>City *</label>
              <input style={{ ...inp, borderColor: errors.city ? '#ff6b6b' : 'var(--border)' }} value={formData.city} onChange={e => set('city', e.target.value)} />
              {errors.city && <span style={errStyle}>{errors.city}</span>}
            </div>
            <div>
              <label style={lbl}>State / Province *</label>
              {stateSelect('state')}
              {errors.state && <span style={errStyle}>{errors.state}</span>}
            </div>
          </div>
          <div style={{ marginBottom: '1rem' }}>
            <label style={lbl}>Geographic Description</label>
            <input style={inp} value={formData.geographic_description} onChange={e => set('geographic_description', e.target.value)} placeholder="e.g. Pacific Northwest, Greater Denver Area" />
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={lbl}>Group Bio *</label>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', fontStyle: 'italic', margin: '0 0 0.4rem 0' }}>Provide a publication-ready bio to be displayed on the website.</p>
            <textarea rows="6" style={{ ...inp, borderColor: errors.group_bio ? '#ff6b6b' : 'var(--border)' }} value={formData.group_bio} onChange={e => set('group_bio', e.target.value)} />
            {errors.group_bio && <span style={errStyle}>{errors.group_bio}</span>}
          </div>
          <div style={{ marginBottom: '1rem' }}>
            <label style={lbl}>Event Types</label>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', fontStyle: 'italic', margin: '0 0 0.4rem 0' }}>Krampusnacht, parades, private events, etc.</p>
            <textarea rows="4" style={inp} value={formData.event_types} onChange={e => set('event_types', e.target.value)} />
          </div>
          <div style={{ marginBottom: '1rem' }}>
            <label style={lbl}>Resources</label>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', fontStyle: 'italic', margin: '0 0 0.4rem 0' }}>Costume makers, prop sources, or other resources you can share.</p>
            <textarea rows="4" style={inp} value={formData.resources} onChange={e => set('resources', e.target.value)} />
          </div>
          <div style={{ marginBottom: '1rem' }}>
            <label style={lbl}>Accepts New Members</label>
            <select style={inp} value={formData.accepts_new_members} onChange={e => set('accepts_new_members', e.target.value)}>
              <option value="">Select...</option>
              <option value="Yes">Yes</option>
              <option value="No">No</option>
              <option value="Sometimes">Sometimes / Contact us</option>
            </select>
          </div>
          <div style={{ marginBottom: '1rem' }}>
            <label style={lbl}>Membership Criteria</label>
            <textarea rows="4" style={inp} value={formData.membership_criteria} onChange={e => set('membership_criteria', e.target.value)} placeholder="Requirements or process for joining your group" />
          </div>
        </div>

        {/* Social Media */}
        <div style={section}>
          <h2 style={{ marginTop: 0 }}>Website & Social Media</h2>
          <div style={{ marginBottom: '1rem' }}>
            <label style={lbl}>Website</label>
            <input type="url" style={inp} value={formData.website} onChange={e => set('website', e.target.value)} placeholder="https://" />
          </div>
          <div style={{ ...gr2, marginBottom: '1rem' }}>
            <div><label style={lbl}>Facebook</label><input style={inp} value={formData.facebook} onChange={e => set('facebook', e.target.value)} placeholder="https://facebook.com/..." /></div>
            <div><label style={lbl}>Instagram</label><input style={inp} value={formData.instagram} onChange={e => set('instagram', e.target.value)} placeholder="@username" /></div>
            <div><label style={lbl}>TikTok</label><input style={inp} value={formData.tiktok} onChange={e => set('tiktok', e.target.value)} placeholder="@username" /></div>
            <div><label style={lbl}>YouTube</label><input style={inp} value={formData.youtube} onChange={e => set('youtube', e.target.value)} placeholder="https://youtube.com/..." /></div>
          </div>
          <div style={{ marginBottom: '1rem' }}>
            <label style={lbl}>Other Social Media</label>
            <input style={inp} value={formData.other_social} onChange={e => set('other_social', e.target.value)} placeholder="Any other social media links" />
          </div>
        </div>

        {/* Privacy */}
        <div style={section}>
          <h2 style={{ marginTop: 0 }}>Privacy Settings</h2>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>Choose what contact information to display on your public profile.</p>
          {[['publish_contact_name','Display contact name on the website'],['publish_contact_email','Display contact email on the website'],['publish_mailing_address','Display mailing address on the website']].map(([field, label]) => (
            <div key={field} style={{ marginBottom: '0.75rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', color: 'var(--text-muted)' }}>
                <input type="checkbox" checked={formData[field]} onChange={e => set(field, e.target.checked)} style={{ width: 'auto' }} />
                {label}
              </label>
            </div>
          ))}
        </div>

        {/* Photos */}
        <div style={section}>
          <h2 style={{ marginTop: 0 }}>Photos *</h2>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', fontStyle: 'italic' }}>Upload 1–3 photos of your group. Accepted format: JPG/JPEG. Maximum 20MB per photo.</p>
          <input type="file" accept=".jpg,.jpeg" multiple onChange={handlePhotoChange} style={{ color: 'var(--text)', marginBottom: '0.5rem' }} />
          {photos.length > 0 && <p style={{ color: '#90ee90', margin: '0.5rem 0 0' }}>{photos.length} photo(s) selected</p>}
          {errors.photos && <p style={{ color: '#ff6b6b', margin: '0.5rem 0 0' }}>{errors.photos}</p>}
        </div>

        {/* Comments */}
        <div style={section}>
          <h2 style={{ marginTop: 0 }}>Additional Comments</h2>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>Anything else you'd like Krampus America to know.</p>
          <textarea rows="4" style={inp} value={formData.comments} onChange={e => set('comments', e.target.value)} />
        </div>

        <div style={{ textAlign: 'center', marginTop: '2rem' }}>
          <button type="submit" disabled={submitting} style={{ padding: '1rem 3rem', background: 'var(--primary)', color: 'white', border: '1px solid var(--primary-light)', borderRadius: '2px', cursor: submitting ? 'not-allowed' : 'pointer', fontFamily: 'Cinzel, serif', fontSize: '1rem', letterSpacing: '0.1em', opacity: submitting ? 0.7 : 1 }}>
            {submitting ? 'Submitting...' : 'Submit Registration'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default GroupRegistrationForm;
